import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';

import StatusBadge from '../components/ui/StatusBadge';
import CapacityBar, { CapacityBarMini } from '../components/ui/CapacityBar';
import { 
  Users, 
  FolderOpen, 
  CheckSquare, 
  ArrowLeft,
  Plus,
  User,
  Calendar,
  Clock,
  Edit,
  Trash2,
  X,
  Search,
  Check
} from 'lucide-react';
const EmployeeSearchField = ({ 
  employees = [], 
  selectedEmployees = [], 
  onEmployeeSelect,
  onEmployeeRemove,
  placeholder = "חפש עובדים...",
  multiple = true,
  showDepartment = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const dropdownRef = useRef(null);

  // Filter employees based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEmployees([]);
      return;
    }

    const filtered = employees.filter(employee => {
      const searchLower = searchTerm.toLowerCase();
      return (
        employee.name?.toLowerCase().includes(searchLower) ||
        employee.email?.toLowerCase().includes(searchLower) ||
        employee.role?.toLowerCase().includes(searchLower)
      );
    }).filter(emp => !selectedEmployees.find(selected => selected.id === emp.id));

    setFilteredEmployees(filtered);
  }, [searchTerm, employees, selectedEmployees]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmployeeSelect = (employee) => {
    onEmployeeSelect(employee);
    setSearchTerm('');
    setIsOpen(false);
    if (!multiple) {
      setFilteredEmployees([]);
    }
  };

  const handleRemoveEmployee = (employeeId) => {
    onEmployeeRemove(employeeId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Employees */}
      {selectedEmployees.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedEmployees.map(employee => (
            <div
              key={employee.id}
              className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
            >
              <User size={14} />
              <span>{employee.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveEmployee(employee.id)}
                className="hover:text-blue-600 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Dropdown Results */}
      {isOpen && searchTerm && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredEmployees.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 text-center">
              לא נמצאו עובדים
            </div>
          ) : (
            filteredEmployees.map(employee => (
              <button
                key={employee.id}
                type="button"
                onClick={() => handleEmployeeSelect(employee)}
                className="w-full px-4 py-3 hover:bg-gray-50 flex items-center justify-between transition-colors text-right"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User size={16} className="text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{employee.name}</div>
                    <div className="text-sm text-gray-500">{employee.email}</div>
                    {employee.role && (
                      <div className="text-xs text-gray-400">
                        {(() => {
                          switch (employee.role) {
                            case 'org_admin':
                              return 'מנהל ראשי';
                            case 'ceo':
                              return 'מנכ"ל';
                            case 'deputy_ceo':
                              return 'סמנכ"ל';
                            case 'dept_manager':
                              return 'מנהל מחלקה';
                            case 'team_manager':
                              return 'מנהל צוות';
                            case 'employee':
                              return 'עובד';
                            default:
                              return employee.role;
                          }
                        })()}
                      </div>
                    )}
                    {showDepartment && employee.departmentName && (
                      <div className="text-xs text-blue-500">{employee.departmentName}</div>
                    )}
                  </div>
                </div>
                {selectedEmployees.find(e => e.id === employee.id) && (
                  <Check size={16} className="text-green-600" />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
const DepartmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const { departments, users, projects, tasks, teams, createProject, createTask, createTeam, updateDepartment, deleteDepartment, updateUser, loading: dataLoading } = useData();

  const [activeTab, setActiveTab] = useState('overview');
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showEditDepartment, setShowEditDepartment] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [showAddManager, setShowAddManager] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [editDepartmentForm, setEditDepartmentForm] = useState({
    name: '',
    description: '',
    managerIds: [],
    teamMembers: [],
    isActive: true
  });
  const [newProject, setNewProject] = useState({ name: '', description: '', scope: 'department', startDate: '', endDate: '', teamId: '' });
  const [newTask, setNewTask] = useState({ title: '', description: '', assigneeId: '', teamId: '', projectId: '', priority: 'important_not_urgent', dueDate: '', estimate: 0, subtasks: [] });
  const [newSubtask, setNewSubtask] = useState('');
  const [newTeam, setNewTeam] = useState({ name: '', managerId: '', teamMembers: [] });

  // Wait for both auth and data to be loaded
  if (authLoading || dataLoading || !currentUser?.role) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען...</p>
        </div>
      </div>
    );
  }

  // Find the department
  const department = departments.find(d => d.id === id);
  
  if (!department) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          המחלקה לא נמצאה
        </div>
      </div>
    );
  }

  // Filter data for this department
  const departmentUsers = users.filter(u => u.departmentId === id);
  const departmentProjects = projects.filter(p => p.departmentId === id);
  const departmentTasks = tasks.filter(t => t.departmentId === id);
  
  // Calculate statistics
  const activeTasks = departmentTasks.filter(t => t.status !== 'הושלם');
  const completedTasks = departmentTasks.filter(t => t.status === 'הושלם');
  const overdueTasks = departmentTasks.filter(t => {
    if (!t.dueDate) return false;
    const dueDate = new Date(t.dueDate);
    const today = new Date();
    return dueDate < today && t.status !== 'הושלם';
  });

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await createProject({
        ...newProject,
        departmentId: id,
        teamId: newProject.teamId || null
      });
      setShowNewProject(false);
      setNewProject({ name: '', description: '', scope: 'department', startDate: '', endDate: '', teamId: '' });
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await createTask({
        ...newTask,
        departmentId: id,
        scope: 'department'
      });
      setShowNewTask(false);
      setNewTask({ title: '', description: '', assigneeId: '', teamId: '', projectId: '', priority: 'important_not_urgent', dueDate: '', estimate: 0, subtasks: [] });
      setNewSubtask('');
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeam.name || !newTeam.managerId) {
      alert('אנא מלא את שם הצוות ומנהל הצוות');
      return;
    }
    
    try {
      // Create the team
      const teamId = await createTeam({
        name: newTeam.name,
        managerId: newTeam.managerId,
        departmentId: id,
        description: '',
        isActive: true
      });
      
      // Add team members to the team
      const updatePromises = [];
      
      // Add all team members
      for (const memberId of newTeam.teamMembers) {
        updatePromises.push(
          updateUser(memberId, { 
            teamId: teamId,
            teamManagerId: newTeam.managerId 
          })
        );
      }
      
      // Also add the manager to the team
      updatePromises.push(
        updateUser(newTeam.managerId, { 
          teamId: teamId,
          teamManagerId: newTeam.managerId 
        })
      );
      
      // Execute all updates in parallel
      await Promise.all(updatePromises);
      
      alert('הצוות נוצר בהצלחה!');
      
      // Close modal and reset
      setShowCreateTeam(false);
      setNewTeam({ name: '', managerId: '', teamMembers: [] });
      
    } catch (error) {
      console.error('Error creating team:', error);
      alert('שגיאה ביצירת הצוות');
    }
  };

  // Handle edit department
  const handleEditDepartment = () => {
    setEditDepartmentForm({
      name: department.name,
      description: department.description || '',
      managerIds: department.managerIds || (department.managerId ? [department.managerId] : []),
      teamMembers: department.teamMembers || [],
      isActive: department.isActive ?? true
    });
    setShowEditDepartment(true);
  };

  // Handle update department
  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    try {
      const departmentData = {
        name: editDepartmentForm.name,
        description: editDepartmentForm.description,
        managerIds: editDepartmentForm.managerIds,
        teamMembers: editDepartmentForm.teamMembers,
        isActive: editDepartmentForm.isActive,
        updatedBy: currentUser.id,
        updatedAt: new Date()
      };
      
      // Get the previous manager IDs to compare
      const previousManagerIds = department.managerIds || (department.managerId ? [department.managerId] : []);
      const newManagerIds = editDepartmentForm.managerIds || [];
      
      // Update the department
      await updateDepartment(id, departmentData);
      
      // Handle manager changes
      const managersToRemove = previousManagerIds.filter(id => !newManagerIds.includes(id));
      const managersToAdd = newManagerIds.filter(id => !previousManagerIds.includes(id));
      
      // Remove previous managers' department assignment
      for (const managerId of managersToRemove) {
        try {
          await updateUser(managerId, { departmentId: null });
        } catch (error) {
          console.error('Error removing manager from department:', error);
        }
      }
      
      // Assign new managers to this department
      for (const managerId of managersToAdd) {
        try {
          await updateUser(managerId, { departmentId: id });
        } catch (error) {
          console.error('Error assigning manager to department:', error);
        }
      }
      
      setShowEditDepartment(false);
      alert('המחלקה עודכנה בהצלחה!');
    } catch (error) {
      console.error('Error updating department:', error);
      alert('שגיאה בעדכון המחלקה');
    }
  };

  // Handle delete department
  const handleDeleteDepartment = async () => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את המחלקה "${department.name}"?`)) {
      try {
        await deleteDepartment(id);
        navigate('/departments');
      } catch (error) {
        console.error('Error deleting department:', error);
        alert('שגיאה במחיקת המחלקה');
      }
    }
  };

  // Handle add team member
  const handleAddTeamMember = async (userId) => {
    try {
      await updateUser(userId, { departmentId: id });
      setShowAddTeamMember(false);
    } catch (error) {
      console.error('Error adding team member:', error);
      alert('שגיאה בהוספת חבר צוות');
    }
  };

  // Handle remove team member
  const handleRemoveTeamMember = async (userId) => {
    if (window.confirm('האם אתה בטוח שברצונך להסיר את חבר הצוות הזה מהמחלקה?')) {
      try {
        await updateUser(userId, { departmentId: null });
      } catch (error) {
        console.error('Error removing team member:', error);
        alert('שגיאה בהסרת חבר צוות');
      }
    }
  };

  // Handle add manager
  const handleAddManager = async (managerId) => {
    try {
      // Add the manager to the department's managerIds array
      const currentManagerIds = department.managerIds || (department.managerId ? [department.managerId] : []);
      if (!currentManagerIds.includes(managerId)) {
        const updatedManagerIds = [...currentManagerIds, managerId];
        await updateDepartment(id, { managerIds: updatedManagerIds });
        
        // Update the user's departmentId
        await updateUser(managerId, { departmentId: id });
        
        setShowAddManager(false);
        alert('המנהל נוסף בהצלחה למחלקה!');
      } else {
        alert('המנהל כבר קיים במחלקה זו');
      }
    } catch (error) {
      console.error('Error adding manager:', error);
      alert('שגיאה בהוספת המנהל');
    }
  };

  // Handle remove manager
  const handleRemoveManager = async (managerId) => {
    if (!window.confirm(`האם אתה בטוח שברצונך להסיר את ${users.find(u => u.id === managerId)?.name || 'המנהל'} מהמחלקה?`)) {
      return;
    }

    try {
      // Remove the manager from the department's managerIds array
      const currentManagerIds = department.managerIds || (department.managerId ? [department.managerId] : []);
      const updatedManagerIds = currentManagerIds.filter(id => id !== managerId);
      
      // Check if we're removing the last manager
      if (updatedManagerIds.length === 0) {
        alert('לא ניתן להסיר את המנהל האחרון מהמחלקה');
        return;
      }

      await updateDepartment(id, { managerIds: updatedManagerIds });
      
      // Remove the user's department assignment
      await updateUser(managerId, { departmentId: null });
      
      alert('המנהל הוסר מהמחלקה בהצלחה!');
    } catch (error) {
      console.error('Error removing manager:', error);
      alert('שגיאה בהסרת המנהל מהמחלקה');
    }
  };

  const tabs = [
    {
      id: 'overview',
      label: 'סקירה כללית',
      icon: Users,
      content: (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">עובדים</p>
                  <p className="text-2xl font-bold mt-1">{departmentUsers.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
                  <Users size={24} />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">פרויקטים</p>
                  <p className="text-2xl font-bold mt-1">{departmentProjects.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 text-green-600">
                  <FolderOpen size={24} />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">משימות פעילות</p>
                  <p className="text-2xl font-bold mt-1">{activeTasks.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
                  <CheckSquare size={24} />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">הושלמו</p>
                  <p className="text-2xl font-bold mt-1">{completedTasks.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
                  <CheckSquare size={24} />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">צוותים</p>
                  <p className="text-2xl font-bold mt-1">{teams.filter(team => team.departmentId === department.id && team.isActive).length}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 text-green-600">
                  <Users size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Department Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">פרטי המחלקה</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">שם המחלקה</p>
                <p className="font-medium">{department.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">תיאור</p>
                <p className="font-medium">{department.description || 'אין תיאור'}</p>
              </div>
                                                           <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">מנהלי מחלקה</p>
                    {(currentUser?.role === 'org_admin' || 
                      (currentUser?.role === 'dept_manager' && (
                        currentUser?.departmentId === department.id || 
                        (department.managerIds && department.managerIds.includes(currentUser?.id)) ||
                        department.managerId === currentUser?.id || 
                        department.createdBy === currentUser?.id
                      ))) && (
                                             <button
                         onClick={() => setShowAddManager(true)}
                         className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors flex items-center gap-1"
                         title="הוסף מנהל נוסף למחלקה"
                       >
                         <Plus size={12} />
                         הוסף מנהל
                       </button>
                    )}
                  </div>
                  <div className="font-medium">
                    {(() => {
                      const managerIds = department.managerIds || (department.managerId ? [department.managerId] : []);
                      if (managerIds.length === 0) return 'לא מוגדרים';
                      
                      return (
                        <div className="flex flex-wrap gap-2">
                          {managerIds.map(managerId => {
                            const manager = users.find(u => u.id === managerId);
                            const isCurrentUser = managerId === currentUser?.id;
                            const canRemove = currentUser?.role === 'org_admin' || 
                              (currentUser?.role === 'dept_manager' && 
                               (department.createdBy === currentUser?.id || 
                                (department.managerIds && department.managerIds.length > 1)));
                            
                            return (
                              <div key={managerId} className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                <span>{manager?.name || 'לא ידוע'}</span>
                                {canRemove && !isCurrentUser && (
                                  <button
                                    onClick={() => handleRemoveManager(managerId)}
                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full p-1 transition-colors"
                                    title="הסר מנהל"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              <div>
                <p className="text-sm text-gray-600">סטטוס</p>
                <p className="font-medium">
                  <span className={`px-2 py-1 rounded text-xs ${department.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {department.isActive ? 'פעילה' : 'לא פעילה'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">פעילות אחרונה</h3>
            <div className="space-y-3">
              {departmentTasks.slice(0, 5).map(task => (
                <div key={task.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                  <div className="flex items-center gap-3">
                    <CheckSquare size={20} className="text-blue-600" />
                    <div>
                      <p className="font-medium">{task.title}</p>
                      <p className="text-sm text-gray-600">{task.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={task.status || 'במסלול'} size="sm" />
                    {task.dueDate && (
                      <span className="text-xs text-gray-500">
                        <Calendar size={14} className="inline mr-1" />
                        {new Date(task.dueDate).toLocaleDateString('he-IL')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {departmentTasks.length === 0 && (
                <p className="text-gray-500 text-center py-4">אין משימות במחלקה זו</p>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'employees',
      label: 'עובדים',
      icon: Users,
      content: (
                 <div className="bg-white rounded-lg shadow">
           <div className="p-6 border-b flex items-center justify-between">
             <h3 className="text-lg font-semibold">עובדי המחלקה</h3>
             <div className="flex items-center gap-3">
               <span className="text-sm text-gray-600">{departmentUsers.length} עובדים</span>

               {(currentUser?.role === 'org_admin' || 
                 (currentUser?.role === 'dept_manager' && (
                   currentUser?.departmentId === department.id || 
                   (department.managerIds && department.managerIds.includes(currentUser?.id)) ||
                   department.managerId === currentUser?.id || 
                   department.createdBy === currentUser?.id
                 ))) && (
                 <button 
                   onClick={() => setShowAddTeamMember(true)}
                   className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 flex items-center gap-2"
                 >
                   <Plus size={16} />
                   הוסף חבר צוות
                 </button>
               )}
             </div>
           </div>
          <div className="p-6">
            {departmentUsers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">אין עובדים במחלקה זו</p>
            ) : (
              <div className="space-y-4">
                {departmentUsers.map(user => {
                  const userTasks = departmentTasks.filter(t => t.assigneeId === user.id);
                  const activeUserTasks = userTasks.filter(t => t.status !== 'הושלם');
                  const totalEstimate = activeUserTasks.reduce((sum, t) => sum + (t.estimate || 0), 0);
                  
                  return (
                                         <div key={user.id} className="border rounded-lg p-4 hover:bg-gray-50">
                       <div className="flex items-center justify-between">
                         <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => navigate(`/admin`, { state: { activeTab: 'users' } })}>
                           <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                             <User size={20} className="text-blue-600" />
                           </div>
                           <div>
                             <p className="font-medium">{user.name}</p>
                             <p className="text-sm text-gray-600">{user.email}</p>
                             <p className="text-xs text-gray-500 capitalize">{(() => {
                               switch (user.role) {
                                 case 'org_admin':
                                   return 'מנהל ראשי';
                                 case 'ceo':
                                   return 'מנכ"ל';
                                 case 'deputy_ceo':
                                   return 'סמנכ"ל';
                                 case 'dept_manager':
                                   return 'מנהל מחלקה';
                                 case 'team_manager':
                                   return 'מנהל צוות';
                                 case 'employee':
                                   return 'עובד';
                                 default:
                                   return 'עובד';
                               }
                             })()}</p>
                           </div>
                         </div>
                         <div className="flex items-center gap-3">
                           <div className="text-right">
                             <p className="text-sm text-gray-600">{activeUserTasks.length} משימות פעילות</p>
                             <CapacityBar 
                               current={totalEstimate} 
                               total={user.weeklyCapacity || 40}
                               showLabel={true}
                             />
                           </div>
                                                       {/* Remove team member button - for org_admin and dept_manager */}
                            {(currentUser?.role === 'org_admin' || 
                              (currentUser?.role === 'dept_manager' && (
                                currentUser?.departmentId === department.id || 
                                (department.managerIds && department.managerIds.includes(currentUser?.id)) ||
                                department.managerId === currentUser?.id || 
                                department.createdBy === currentUser?.id
                              ))) && (
                             <button 
                               onClick={(e) => {
                                 e.stopPropagation();
                                 handleRemoveTeamMember(user.id);
                               }}
                               className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                               title="הסר מהמחלקה"
                             >
                               <Trash2 size={16} />
                             </button>
                           )}
                         </div>
                       </div>
                     </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'teams',
      label: 'צוותים',
      icon: Users,
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold">צוותי המחלקה</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {teams.filter(team => team.departmentId === department.id).length} צוותים
              </span>
              {(currentUser?.role === 'org_admin' || 
                (currentUser?.role === 'dept_manager' && (
                  currentUser?.departmentId === department.id || 
                  (department.managerIds && department.managerIds.includes(currentUser?.id)) ||
                  department.managerId === currentUser?.id || 
                  department.createdBy === currentUser?.id
                ))) && (
                <button 
                  onClick={() => setShowCreateTeam(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={16} />
                  צור צוות חדש
                </button>
              )}
            </div>
          </div>
          <div className="p-6">
            {(() => {
              const departmentTeams = teams.filter(team => team.departmentId === department.id);
              if (departmentTeams.length === 0) {
                return (
                  <p className="text-gray-500 text-center py-8">אין צוותים במחלקה זו</p>
                );
              }
              
              return (
                <div className="space-y-4">
                  {departmentTeams.map(team => {
                    const teamMembers = users.filter(user => {
                      // Check new multi-team structure
                      if (user.teamIds && Array.isArray(user.teamIds)) {
                        return user.teamIds.includes(team.id);
                      }
                      
                      // Fallback to old single teamId for backward compatibility
                      if (user.teamId) {
                        return user.teamId === team.id;
                      }
                      
                      return false;
                    });
                    const teamManager = users.find(user => user.id === team.managerId);
                    
                    return (
                      <div key={team.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => navigate(`/teams/${team.id}`)}>
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <Users size={20} className="text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">{team.name}</p>
                              <p className="text-sm text-gray-600">
                                מנהל: {teamManager?.name || 'לא מוגדר'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {teamMembers.length} חברי צוות
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm text-gray-600">סטטוס</p>
                              <p className="font-medium">
                                <span className={`px-2 py-1 rounded text-xs ${team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {team.isActive ? 'פעיל' : 'לא פעיל'}
                                </span>
                              </p>
                            </div>
                            {(currentUser?.role === 'org_admin' || 
                              (currentUser?.role === 'dept_manager' && (
                                currentUser?.departmentId === department.id || 
                                (department.managerIds && department.managerIds.includes(currentUser?.id)) ||
                                department.managerId === currentUser?.id || 
                                department.createdBy === currentUser?.id
                              ))) && (
                              <button
                                onClick={() => navigate(`/teams/${team.id}`)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="ערוך צוות"
                              >
                                <Edit size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )
    },
    {
      id: 'projects',
      label: 'פרויקטים',
      icon: FolderOpen,
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold">פרויקטי המחלקה</h3>
            <button 
              onClick={() => setShowNewProject(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus size={16} />
              פרויקט חדש
            </button>
          </div>
          <div className="p-6">
            {departmentProjects.length === 0 ? (
              <p className="text-gray-500 text-center py-8">אין פרויקטים במחלקה זו</p>
            ) : (
              <div className="space-y-4">
                {departmentProjects.map(project => {
                  const projectTasks = departmentTasks.filter(t => t.projectId === project.id);
                  const completedProjectTasks = projectTasks.filter(t => t.status === 'הושלם');
                  
                  return (
                    <div key={project.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{project.name}</h4>
                            {/* Department Badge - redundant here but good for consistency */}
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                              {department.name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{project.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>תחום: {project.scope === 'personal' ? 'אישי' : 'ניהולי'}</span>
                            {project.startDate && (
                              <span>התחלה: {new Date(project.startDate).toLocaleDateString('he-IL')}</span>
                            )}
                            {project.endDate && (
                              <span>סיום: {new Date(project.endDate).toLocaleDateString('he-IL')}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <StatusBadge 
                            status={(() => {
                              if (projectTasks.length === 0) return 'ממתין לעבודה';
                              if (completedProjectTasks.length === projectTasks.length) return 'פרויקט הושלם';
                              
                              const activeTasks = projectTasks.filter(t => t.status !== 'הושלם');
                              if (activeTasks.length > 0) return 'פרויקט בתהליך';
                              
                              return 'ממתין לעבודה';
                            })()} 
                            size="sm" 
                          />
                          <div className="text-sm text-gray-600 mt-1">
                            {projectTasks.length} משימות
                          </div>
                          <CapacityBarMini 
                            current={completedProjectTasks.length} 
                            total={projectTasks.length || 1} 
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'tasks',
      label: 'משימות',
      icon: CheckSquare,
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold">משימות המחלקה</h3>
            <button 
              onClick={() => setShowNewTask(true)}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 flex items-center gap-2"
            >
              <Plus size={16} />
              משימה חדשה
            </button>
          </div>
          <div className="p-6">
            {departmentTasks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">אין משימות במחלקה זו</p>
            ) : (
              <div className="space-y-4">
                {departmentTasks.map(task => (
                  <div key={task.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{task.title}</h4>
                        <p className="text-sm text-gray-600">{task.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          {task.assigneeId && (
                            <span>מוקצה: {users.find(u => u.id === task.assigneeId)?.name || 'לא ידוע'}</span>
                          )}
                          {task.teamId && (
                            <span>צוות: {teams.find(t => t.id === task.teamId)?.name || 'לא ידוע'}</span>
                          )}
                          {task.projectId && (
                            <span>פרויקט: {projects.find(p => p.id === task.projectId)?.name || 'לא ידוע'}</span>
                          )}
                          {task.dueDate && (
                            <span>דדליין: {new Date(task.dueDate).toLocaleDateString('he-IL')}</span>
                          )}
                          {task.estimate > 0 && (
                            <span>הערכה: {task.estimate} שעות</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <StatusBadge status={task.status || 'במסלול'} size="sm" />
                        <div className="text-sm text-gray-600 mt-1">
                          {task.priority === 'important_urgent' ? 'חשוב ודחוף' :
                           task.priority === 'important_not_urgent' ? 'חשוב ולא דחוף' :
                           task.priority === 'not_important_urgent' ? 'דחוף ולא חשוב' :
                           task.priority === 'not_important_not_urgent' ? 'לא חשוב ולא דחוף' :
                           task.priority}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{department.name}</h1>
            <p className="text-gray-600">פרטי המחלקה</p>
          </div>
        </div>
        
        {/* Action buttons - for org_admin and dept_manager */}
        {(currentUser?.role === 'org_admin' || 
          (currentUser?.role === 'dept_manager' && (
            currentUser?.departmentId === department.id || 
            (department.managerIds && department.managerIds.includes(currentUser?.id)) ||
            department.managerId === currentUser?.id || 
            department.createdBy === currentUser?.id
          ))) && (
          <div className="flex gap-2">
            <button 
              onClick={handleEditDepartment}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="ערוך מחלקה"
            >
              <Edit size={16} />
              ערוך מחלקה
            </button>
            <button 
              onClick={handleDeleteDepartment}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              title="מחק מחלקה"
            >
              <Trash2 size={16} />
              מחק מחלקה
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <tab.icon size={20} />
                  {tab.label}
                </div>
              </button>
            ))}
          </nav>
        </div>
        <div className="p-6">
          {tabs.find(tab => tab.id === activeTab)?.content}
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">פרויקט חדש במחלקה</h3>
            <form onSubmit={handleCreateProject}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <input 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="שם הפרויקט" 
                    value={newProject.name} 
                    onChange={(e)=>setNewProject({...newProject,name:e.target.value})} 
                    required
                  />
                  <textarea 
                    className="w-full px-3 py-2 border rounded h-24" 
                    placeholder="תיאור הפרויקט" 
                    value={newProject.description} 
                    onChange={(e)=>setNewProject({...newProject,description:e.target.value})} 
                  />
                  <select 
                    className="w-full px-3 py-2 border rounded" 
                    value={newProject.scope} 
                    onChange={(e)=>setNewProject({...newProject,scope:e.target.value})}
                  >
                    <option value="department">מחלקתי</option>
                    <option value="organization">ארגוני</option>
                  </select>
                  
                  {/* Team Selection */}
                  <select 
                    className="w-full px-3 py-2 border rounded" 
                    value={newProject.teamId} 
                    onChange={(e)=>setNewProject({...newProject,teamId:e.target.value})}
                  >
                    <option value="">בחר צוות אחראי (אופציונלי)</option>
                    {teams.filter(team => team.departmentId === id && team.isActive).map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-3">
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="תאריך התחלה" 
                    value={newProject.startDate} 
                    onChange={(e)=>setNewProject({...newProject,startDate:e.target.value})} 
                  />
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="תאריך סיום" 
                    value={newProject.endDate} 
                    onChange={(e)=>setNewProject({...newProject,endDate:e.target.value})} 
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button 
                  type="button"
                  className="px-4 py-2 text-gray-600 hover:text-gray-800" 
                  onClick={()=>setShowNewProject(false)}
                >
                  בטל
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  צור פרויקט
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">משימה חדשה במחלקה</h3>
            <form onSubmit={handleCreateTask}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <input 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="כותרת המשימה" 
                    value={newTask.title} 
                    onChange={(e)=>setNewTask({...newTask,title:e.target.value})} 
                    required
                  />
                  <textarea 
                    className="w-full px-3 py-2 border rounded h-24" 
                    placeholder="תיאור המשימה" 
                    value={newTask.description} 
                    onChange={(e)=>setNewTask({...newTask,description:e.target.value})} 
                  />
                  <select 
                    className="w-full px-3 py-2 border rounded"
                    value={newTask.priority}
                    onChange={(e)=>setNewTask({...newTask,priority:e.target.value})}
                  >
                    <option value="important_urgent">חשוב ודחוף</option>
                    <option value="important_not_urgent">חשוב ולא דחוף</option>
                    <option value="not_important_urgent">דחוף ולא חשוב</option>
                    <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <select 
                    className="w-full px-3 py-2 border rounded"
                    value={newTask.assigneeId}
                    onChange={(e)=>setNewTask({...newTask,assigneeId:e.target.value})}
                  >
                    <option value="">בחר מוקצה</option>
                    {departmentUsers.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <select 
                    className="w-full px-3 py-2 border rounded"
                    value={newTask.teamId}
                    onChange={(e)=>setNewTask({...newTask,teamId:e.target.value})}
                  >
                    <option value="">הקצאה לצוות (אופציונלי)</option>
                    {teams.filter(team => team.departmentId === department.id && team.isActive).map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                  <select 
                    className="w-full px-3 py-2 border rounded"
                    value={newTask.projectId}
                    onChange={(e)=>setNewTask({...newTask,projectId:e.target.value})}
                  >
                    <option value="">בחר פרויקט (אופציונלי)</option>
                    {projects.filter(project => project.departmentId === department.id).map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="דדליין" 
                    value={newTask.dueDate} 
                    onChange={(e)=>setNewTask({...newTask,dueDate:e.target.value})} 
                  />
                  <input 
                    type="number" 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="הערכת שעות" 
                    value={newTask.estimate} 
                    onChange={(e)=>setNewTask({...newTask,estimate:parseInt(e.target.value) || 0})} 
                  />
                </div>
              </div>
              
              {/* Subtasks Section */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">תתי משימות</label>
                <div className="space-y-3">
                  {/* Existing Subtasks */}
                  {newTask.subtasks && newTask.subtasks.length > 0 && (
                    <div className="space-y-2">
                      {newTask.subtasks.map((subtask, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                          <span className="flex-1 text-sm">{subtask}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedSubtasks = newTask.subtasks.filter((_, i) => i !== index);
                              setNewTask({...newTask, subtasks: updatedSubtasks});
                            }}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="מחק תת משימה"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add New Subtask */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="הוסף תת משימה חדשה..."
                      value={newSubtask || ''}
                      onChange={(e) => setNewSubtask(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newSubtask.trim()) {
                          const updatedSubtasks = [...(newTask.subtasks || []), newSubtask.trim()];
                          setNewTask({...newTask, subtasks: updatedSubtasks});
                          setNewSubtask('');
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newSubtask && newSubtask.trim()) {
                          const updatedSubtasks = [...(newTask.subtasks || []), newSubtask.trim()];
                          setNewTask({...newTask, subtasks: updatedSubtasks});
                          setNewSubtask('');
                        }
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-1"
                    >
                      <Plus size={14} />
                      הוסף
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button 
                  type="button"
                  className="px-4 py-2 text-gray-600 hover:text-gray-800" 
                  onClick={()=>setShowNewTask(false)}
                >
                  בטל
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  צור משימה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {showEditDepartment && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowEditDepartment(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">ערוך מחלקה</h3>
              <button
                onClick={() => setShowEditDepartment(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateDepartment}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם מחלקה
                  </label>
                  <input
                    type="text"
                    value={editDepartmentForm.name}
                    onChange={(e) => setEditDepartmentForm({ ...editDepartmentForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תיאור (אופציונלי)
                  </label>
                  <textarea
                    value={editDepartmentForm.description}
                    onChange={(e) => setEditDepartmentForm({ ...editDepartmentForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                  />
                </div>

                                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    מנהלי מחלקה (אופציונלי)
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-[120px] overflow-y-auto bg-gray-50">
                    {users.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map((user) => (
                      <label key={user.id} className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editDepartmentForm.managerIds?.includes(user.id) || false}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // הוסף מנהל
                              setEditDepartmentForm(prevState => ({
                                ...prevState,
                                managerIds: [...(prevState.managerIds || []), user.id]
                              }));
                            } else {
                              // הסר מנהל
                              setEditDepartmentForm(prevState => ({
                                ...prevState,
                                managerIds: (prevState.managerIds || []).filter(id => id !== user.id)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          {user.name} ({(() => {
                            switch (user.role) {
                              case 'org_admin':
                                return 'מנהל ראשי';
                              case 'ceo':
                                return 'מנכ"ל';
                              case 'deputy_ceo':
                                return 'סמנכ"ל';
                              case 'dept_manager':
                                return 'מנהל מחלקה';
                              case 'team_manager':
                                return 'מנהל צוות';
                              default:
                                return 'עובד';
                            }
                          })()})
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    לחץ על כל מנהל שברצונך להוסיף
                    <br />
                    <span className="text-blue-600">נבחרו: {editDepartmentForm.managerIds?.length || 0} מנהלים</span>
                  </p>
                </div>
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    חברי צוות (אופציונלי)
  </label>
  <p className="text-xs text-gray-500 mb-2">
    עובדים במחלקה: {users.filter(u => u.departmentId === department.id).length} | 
    עובדים פעילים: {users.filter(u => u.departmentId === department.id && u.isActive).length} | 
    עובדים ללא צוות: {users.filter(u => u.departmentId === department.id && u.isActive && !u.teamId).length}
  </p>
  <EmployeeSearchField
    employees={users.filter(user => 
      user.isActive && 
      user.departmentId === department.id &&
      !user.teamId
    )}
    selectedEmployees={users.filter(user => newTeam.teamMembers?.includes(user.id))}
    onEmployeeSelect={(employee) => {
      setNewTeam(prevState => ({
        ...prevState,
        teamMembers: [...(prevState.teamMembers || []), employee.id]
      }));
    }}
    onEmployeeRemove={(employeeId) => {
      setNewTeam(prevState => ({
        ...prevState,
        teamMembers: (prevState.teamMembers || []).filter(id => id !== employeeId)
      }));
    }}
    placeholder="חפש עובדים להוספה לצוות..."
  />
</div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editDepartmentForm.isActive}
                      onChange={(e) => setEditDepartmentForm({ ...editDepartmentForm, isActive: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">מחלקה פעילה</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditDepartment(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  עדכן מחלקה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Team Member Modal */}
      {showAddTeamMember && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowAddTeamMember(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">הוסף חבר צוות למחלקה</h3>
              <button
                onClick={() => setShowAddTeamMember(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                בחר עובד או מנהל להוספה למחלקה "{department.name}"
              </p>

              {/* Available employees (not in any department or in different department) */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">עובדים ומנהלים זמינים:</h4>
                {users
                  .filter(user => 
                    user.isActive && 
                    (!user.departmentId || user.departmentId !== id) &&
                    user.role !== 'org_admin' &&
                    user.role !== 'ceo' &&
                    user.role !== 'deputy_ceo'
                  )
                  .map(user => (
                    <div key={user.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User size={16} className="text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{user.name}</p>
                          <p className="text-xs text-gray-600">{user.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              {(() => {
                                switch (user.role) {
                                  case 'dept_manager':
                                    return 'מנהל מחלקה';
                                  case 'team_manager':
                                    return 'מנהל צוות';
                                  case 'employee':
                                    return 'עובד';
                                  default:
                                    return 'עובד';
                                }
                              })()}
                            </span>
                            {user.departmentId && (
                              <span className="text-xs text-orange-600">
                                במחלקה: {departments.find(d => d.id === user.departmentId)?.name || 'לא ידוע'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddTeamMember(user.id)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        הוסף
                      </button>
                    </div>
                  ))}
                
                {users.filter(user => 
                  user.isActive && 
                  (!user.departmentId || user.departmentId !== id) &&
                  user.role !== 'org_admin' &&
                  user.role !== 'ceo' &&
                  user.role !== 'deputy_ceo'
                ).length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    אין עובדים זמינים להוספה למחלקה זו
                  </p>
                )}
              </div>

              {/* Current team members */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">עובדים ומנהלים נוכחיים:</h4>
                {departmentUsers.map(user => (
                  <div key={user.id} className="flex items-center gap-3 border rounded-lg p-3 bg-blue-50">
                    <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
                      <User size={16} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-gray-600">{user.email}</p>
                    </div>
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                      {(() => {
                        switch (user.role) {
                          case 'dept_manager':
                            return 'מנהל מחלקה';
                          case 'team_manager':
                            return 'מנהל צוות';
                          case 'employee':
                            return 'עובד';
                          default:
                            return 'עובד';
                        }
                      })()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAddTeamMember(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                סגור
              </button>
            </div>
                     </div>
         </div>
       )}

       {/* Add Manager Modal */}
       {showAddManager && (
         <div 
           className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
           onClick={() => setShowAddManager(false)}
         >
           <div 
             className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
             onClick={(e) => e.stopPropagation()}
           >
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-medium text-gray-900">הוסף מנהל למחלקה</h3>
               <button
                 onClick={() => setShowAddManager(false)}
                 className="text-gray-400 hover:text-gray-600"
               >
                 <X className="w-5 h-5" />
               </button>
             </div>

             <div className="space-y-4">
               <p className="text-sm text-gray-600 mb-4">
                 בחר מנהל להוספה למחלקה "{department.name}"
               </p>

               {/* Available managers */}
               <div className="space-y-3">
                 <h4 className="font-medium text-gray-700">מנהלים זמינים:</h4>
                 {users
                   .filter(user => 
                     (user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager') && 
                     user.isActive &&
                     !(department.managerIds && department.managerIds.includes(user.id)) &&
                     !(department.managerId === user.id)
                   )
                   .map(user => (
                     <div key={user.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
                       <div className="flex items-center gap-3">
                         <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                           <User size={16} className="text-blue-600" />
                         </div>
                         <div>
                           <p className="font-medium text-sm">{user.name}</p>
                           <p className="text-xs text-gray-600">{user.email}</p>
                           <p className="text-xs text-blue-600">
                             {(() => {
                               switch (user.role) {
                                 case 'org_admin':
                                   return 'מנהל ראשי';
                                 case 'ceo':
                                   return 'מנכ"ל';
                                 case 'deputy_ceo':
                                   return 'סמנכ"ל';
                                 case 'dept_manager':
                                   return 'מנהל מחלקה';
                                 case 'team_manager':
                                   return 'מנהל צוות';
                                 default:
                                   return 'עובד';
                               }
                             })()}
                           </p>
                           {user.departmentId && (
                             <p className="text-xs text-orange-600">
                               מחלקה נוכחית: {departments.find(d => d.id === user.departmentId)?.name || 'לא ידוע'}
                             </p>
                           )}
                         </div>
                       </div>
                       <button
                         onClick={() => handleAddManager(user.id)}
                         className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                       >
                         הוסף
                       </button>
                     </div>
                   ))}
                 
                 {users.filter(user => 
                   (user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager') && 
                   user.isActive &&
                   !(department.managerIds && department.managerIds.includes(user.id)) &&
                   !(department.managerId === user.id)
                 ).length === 0 && (
                   <p className="text-gray-500 text-center py-4">
                     אין מנהלים זמינים להוספה למחלקה זו
                   </p>
                 )}
               </div>

               {/* Current managers */}
               <div className="space-y-3">
                 <h4 className="font-medium text-gray-700">מנהלים נוכחיים:</h4>
                 {(() => {
                   const currentManagerIds = department.managerIds || (department.managerId ? [department.managerId] : []);
                   if (currentManagerIds.length === 0) {
                     return <p className="text-gray-500 text-center py-2">אין מנהלים מוגדרים</p>;
                   }
                   
                   return currentManagerIds.map(managerId => {
                     const manager = users.find(u => u.id === managerId);
                     if (!manager) return null;
                     
                     return (
                       <div key={manager.id} className="flex items-center gap-3 border rounded-lg p-3 bg-blue-50">
                         <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
                           <User size={16} className="text-blue-600" />
                         </div>
                         <div className="flex-1">
                           <p className="font-medium text-sm">{manager.name}</p>
                           <p className="text-xs text-gray-600">{manager.email}</p>
                         </div>
                         <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                           מנהל
                         </span>
                       </div>
                     );
                   });
                 })()}
               </div>
             </div>

             <div className="flex justify-end mt-6">
               <button
                 onClick={() => setShowAddManager(false)}
                 className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
               >
                 סגור
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Create Team Modal */}
       {showCreateTeam && (
         <div 
           className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
           onClick={() => setShowCreateTeam(false)}
         >
           <div 
             className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
             onClick={(e) => e.stopPropagation()}
           >
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-medium text-gray-900">צור צוות חדש</h3>
               <button
                 onClick={() => setShowCreateTeam(false)}
                 className="text-gray-400 hover:text-gray-600"
               >
                 <X className="w-5 h-5" />
               </button>
             </div>

             <form onSubmit={handleCreateTeam}>
               <div className="space-y-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     שם הצוות
                   </label>
                   <input
                     type="text"
                     value={newTeam.name}
                     onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                     required
                   />
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     מנהל הצוות
                   </label>
                   <select
                     value={newTeam.managerId}
                     onChange={(e) => setNewTeam({ ...newTeam, managerId: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                     required
                   >
                     <option value="">בחר מנהל צוות</option>
                     {users.filter(user => 
                       (user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager') && 
                       user.isActive
                     ).map((user) => (
                       <option key={user.id} value={user.id}>
                         {user.name} ({(() => {
                           switch (user.role) {
                             case 'org_admin':
                               return 'מנהל ראשי';
                             case 'ceo':
                               return 'מנכ"ל';
                             case 'deputy_ceo':
                               return 'סמנכ"ל';
                             case 'dept_manager':
                               return 'מנהל מחלקה';
                             case 'team_manager':
                               return 'מנהל צוות';
                             default:
                               return 'עובד';
                           }
                         })()})
                       </option>
                     ))}
                   </select>
                 </div>
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    חברי צוות (אופציונלי)
  </label>
  <EmployeeSearchField
    employees={users.filter(user => user.role === 'employee' && user.isActive)}
    selectedEmployees={users.filter(user => editDepartmentForm.teamMembers?.includes(user.id))}
    onEmployeeSelect={(employee) => {
      setEditDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: [...(prevState.teamMembers || []), employee.id]
      }));
    }}
    onEmployeeRemove={(employeeId) => {
      setEditDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: (prevState.teamMembers || []).filter(id => id !== employeeId)
      }));
    }}
    placeholder="חפש עובדים להוספה למחלקה..."
  />
</div>
               </div>

               <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                 <button
                   type="button"
                   onClick={() => setShowCreateTeam(false)}
                   className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                 >
                   בטל
                 </button>
                 <button
                   type="submit"
                   className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                 >
                   צור צוות
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}
     </div>
   );
 };

export default DepartmentDetail; 